import { useState, useEffect } from "react";
import { Contrat, Bien, Locataire, ContratTemplate } from "../../model/Model";
import { getBiensDisponibles, getLocatairesDisponibles, getContratById } from "../../service/contrats/contrats.service";
import { getTemplates } from "../../service/templates/templates.service";

interface AjouterContratPopupProps {
    ajouterModifier: (contrat: Contrat) => Promise<void>;
    onClose: () => void;
    contratId?: number;
}

export function AjouterContratPopup({ ajouterModifier, onClose, contratId }: AjouterContratPopupProps) {
    const [contrat, setContrat] = useState<Contrat>({
        bienId: 0,
        locataireId: 0,
        templateId: 0,
        dateDeDebut: "",
        dateDeFin: "",
        montantLoyer: 0,
        montantCharges: 0,
        montantDepot: 0,
        statut: 'ACTIF',
        identifiant: 'identifiant'
    });

    const [biens, setBiens] = useState<Bien[]>([]);
    const [locataires, setLocataires] = useState<Locataire[]>([]);
    const [templates, setTemplates] = useState<ContratTemplate[]>([]);

    useEffect(() => {
        async function fetchData() {
            const [biensData, locatairesData, templatesData] = await Promise.all([
                getBiensDisponibles(),
                getLocatairesDisponibles(),
                getTemplates()
            ]);
            setBiens(biensData);
            setLocataires(locatairesData);
            setTemplates(templatesData);
        }
        fetchData();
    }, []);

    useEffect(() => {
        async function fetchContrat() {
            if (!contratId) return;
            const data = await getContratById(contratId);
            if (data) {
                setContrat(data);
            }
        }
        fetchContrat();
    }, [contratId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await ajouterModifier(contrat);
        // Reset form
        setContrat({
            contratId: 0,
            bienId: 0,
            locataireId: 0,
            templateId: 0,
            dateDeDebut: "",
            dateDeFin: "",
            montantLoyer: 0,
            montantCharges: 0,
            frequenceDePaiement: 0,
            montantDepot: 0,
            statut: 'ACTIF'
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setContrat(prev => ({
            ...prev,
            [name]: name === 'bienId' || name === 'locataireId' || name === 'templateId' || name === 'loyer' || name === 'charges' || name === 'depot' 
                ? Number(value) 
                : value
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {contratId ? "Modifier le contrat" : "Créer un nouveau contrat"}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bien *
                        </label>
                        <select
                            name="bienId"
                            value={contrat.bienId}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value={0}>Sélectionnez un bien</option>
                            {biens.map(bien => (
                                <option key={bien.bienId} value={bien.bienId}>
                                    {bien.identifiant} - {bien.adresse} - {bien.ville} ({bien.type})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Locataire *
                        </label>
                        <select
                            name="locataireId"
                            value={contrat.locataireId}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value={0}>Sélectionnez un locataire</option>
                            {locataires.map(locataire => (
                                <option key={locataire.locataireId} value={locataire.locataireId || 0}>
                                    {locataire.prenom} {locataire.nom}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Template de contrat *
                        </label>
                        <select
                            name="templateId"
                            value={contrat.templateId}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value={0}>Sélectionnez un template</option>
                            {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.nom} ({template.type})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Le template définit le modèle du contrat qui sera généré
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date de début *
                            </label>
                            <input
                                type="date"
                                name="dateDeDebut"
                                value={contrat.dateDeDebut}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date de fin
                            </label>
                            <input
                                type="date"
                                name="dateDeFin"
                                value={contrat.dateDeFin}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Loyer (€) *
                            </label>
                            <input
                                type="number"
                                name="montantLoyer"
                                value={contrat.montantLoyer}
                                onChange={handleInputChange}
                                required
                                min="0"
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Charges (€)
                            </label>
                            <input
                                type="number"
                                name="montantCharges"
                                value={contrat.montantCharges}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Dépôt (€)
                            </label>
                            <input
                                type="number"
                                name="montantDepot"
                                value={contrat.montantDepot}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Statut
                        </label>
                        <select
                            name="statut"
                            value={contrat.statut}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="ACTIF">Actif</option>
                            <option value="EXPIRE">Expiré</option>
                            <option value="RESILIÉ">Résilié</option>
                        </select>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition duration-200"
                        >
                            Annuler
                        </button>
                        {contratId ? (
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition duration-200"
                            >
                                Modifier le contrat
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition duration-200"
                            >
                                Créer le contrat
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}